<script>
export default {
  props: {
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: "bx-crown",
    },
    variant: {
      type: String,
      default: "green",
    },
    levels: {
      type: Array,
      default: () => [],
    },
    discounts: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    trackClass() {
      return "evolution-track evolution-" + this.variant;
    },
  },
  methods: {
    // Status usado na exibição: o primeiro nível da trilha nunca aparece como
    // bloqueado — enquanto não for conquistado, ele é o nível em andamento
    displayStatus(index) {
      const level = this.levels[index];

      if (!level) {
        return "locked";
      }
      if (index === 0 && level.status !== "achieved") {
        return "progress";
      }
      return level.status;
    },
    // Classe do nó conforme o status: achieved | progress | locked
    nodeClass(status) {
      return "evolution-node is-" + status;
    },
    // Ícone interno do nó
    nodeIcon(status) {
      if (status === "achieved") {
        return "bx bx-check";
      } else if (status === "progress") {
        return "bx bx-play";
      }
      return "bx bx-hourglass";
    },
    // Segmento entre o nó do índice e o próximo: só fica preenchido quando os
    // dois níveis foram conquistados. O segmento que chega num nó em andamento
    // fica vazio, pois o nível ainda não foi atingido
    connectorClass(index) {
      const current = this.levels[index];
      const next = this.levels[index + 1];

      if (!current || !next) {
        return "evolution-connector is-empty";
      }
      if (current.status === "achieved" && next.status === "achieved") {
        return "evolution-connector is-full";
      }
      return "evolution-connector is-empty";
    },
    // Percentual de desconto do nível na posição informada (usado no mobile)
    discountAt(index) {
      const discount = this.discounts[index];
      return discount ? discount.percent : null;
    },
    // No mobile o desconto é lido por trilha: só fica ativo quando o nível
    // daquela posição foi conquistado nesta trilha
    // (no desktop a faixa superior usa a regra geral, de todas as trilhas)
    isDiscountLocked(index) {
      const level = this.levels[index];
      return !level || level.status !== "achieved";
    },
  },
};
</script>

<template>
  <div :class="trackClass">
    <!-- Rótulo da trilha -->
    <div class="evolution-label">
      <span class="evolution-label-icon">
        <i :class="'bx ' + icon"></i>
      </span>
      <span class="evolution-label-text notranslate">{{ label }}</span>
    </div>

    <!-- Níveis -->
    <div class="evolution-levels">
      <div v-for="(level, index) in levels" :key="index" class="evolution-step">
        <div class="evolution-step-mark">
          <span :class="nodeClass(displayStatus(index))">
            <i :class="nodeIcon(displayStatus(index))"></i>
          </span>
          <span v-if="index < levels.length - 1" :class="connectorClass(index)"></span>
        </div>

        <div class="evolution-step-info">
          <span class="evolution-step-name notranslate" :class="{ 'is-locked': displayStatus(index) === 'locked' }">{{ level.name }}</span>
          <span v-if="discountAt(index)" class="evolution-step-discount" :class="{ 'is-locked': isDiscountLocked(index) }">
            <span class="evolution-badge">{{ index + 1 }}</span>
            <span class="evolution-percent">{{ discountAt(index) }}%</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== Cores por variante ===== */
.evolution-green {
  --track-color: #0f9d58;
  --track-soft: #e8f5ee;
  --track-faded: #d6ece0;
}
.evolution-amber {
  --track-color: #e6a417;
  --track-soft: #fdf3e2;
  --track-faded: #f6e6c8;
}
.evolution-indigo {
  --track-color: #3b2fe8;
  --track-soft: #eceafe;
  --track-faded: #dedbfb;
}

/* ===== Rótulo ===== */
.evolution-label {
  display: flex;
  align-items: center;
  background: var(--track-soft);
  border-radius: 12px;
  padding: 12px 16px;
}
.evolution-label-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 100%;
  background: var(--track-color);
  color: #fff;
  font-size: 22px;
}
.evolution-label-text {
  margin-left: 12px;
  font-weight: 600;
  color: #343a40;
  line-height: 1.2;
}

/* ===== Nós ===== */
.evolution-node {
  /* acima do conector, que passa por trás do círculo */
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 100%;
  font-size: 24px;
  border: 2px solid transparent;
}
.evolution-node.is-achieved {
  background: var(--track-color);
  color: #fff;
}
.evolution-node.is-progress {
  background: #fff;
  border-color: var(--track-color);
  color: var(--track-color);
}
.evolution-node.is-locked {
  background: #fff;
  border-color: #e2e5e9;
  color: #adb5bd;
}

.evolution-step-name {
  font-weight: 600;
  color: #343a40;
}
.evolution-step-name.is-locked {
  color: #adb5bd;
}

.evolution-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 5px;
  background: #0f9d58;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}
.evolution-percent {
  margin-left: 6px;
  font-weight: 600;
  color: #0f9d58;
}
/* Desconto ainda não liberado: mesma leitura cinza dos níveis bloqueados */
.evolution-step-discount.is-locked .evolution-badge {
  background: #e9ecef;
  color: #adb5bd;
}
.evolution-step-discount.is-locked .evolution-percent {
  color: #adb5bd;
}

/* ===== Desktop: trilha horizontal ===== */
@media (min-width: 768px) {
  .evolution-track {
    display: flex;
    align-items: center;
    padding: 20px 0 0;
  }
  .evolution-label {
    width: 250px;
    min-width: 250px;
  }
  .evolution-levels {
    display: flex;
    flex: 1;
    padding-left: 24px;
  }
  /* 4 colunas de mesma largura, com nó e nome centralizados em cada uma */
  .evolution-step {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    min-width: 0;
  }
  .evolution-step-mark {
    display: flex;
    align-items: center;
  }
  /* O conector vai do centro deste nó ao centro do próximo (uma coluna à
     direita), passando por trás do círculo */
  .evolution-connector {
    position: absolute;
    top: 21px;
    left: 50%;
    width: 100%;
    height: 3px;
    background: var(--track-faded);
  }
  .evolution-connector.is-full {
    background: var(--track-color);
  }
  .evolution-connector.is-empty {
    background: var(--track-faded);
  }
  .evolution-step-info {
    text-align: center;
  }
  .evolution-step-name {
    display: block;
    white-space: nowrap;
    font-size: 13px;
  }
  /* No desktop o desconto aparece na faixa superior da view */
  .evolution-step-discount {
    display: none;
  }
}

/* ===== Mobile: timeline vertical ===== */
@media (max-width: 767.98px) {
  .evolution-track {
    border: 1px solid #eff0f2;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  .evolution-label {
    border-radius: 0;
  }
  .evolution-levels {
    padding: 16px;
  }
  .evolution-step {
    display: flex;
    align-items: flex-start;
  }
  .evolution-step-mark {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 46px;
  }
  .evolution-connector {
    width: 3px;
    height: 22px;
    background: var(--track-faded);
  }
  .evolution-connector.is-full {
    background: var(--track-color);
  }
  .evolution-connector.is-empty {
    background: var(--track-faded);
  }
  .evolution-step-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    min-width: 0;
    /* alinha verticalmente com o centro do nó (46px) */
    height: 46px;
    padding-left: 14px;
  }
  .evolution-step-name {
    font-size: 15px;
  }
  .evolution-step-discount {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding-left: 8px;
  }
}
</style>
